import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export interface StudioRetentionCohortRow {
  cohortPeriodId: number
  cohortLabel: string
  cohortSize: number
  /** Смещение в ПЕРИОДАХ сети (28/35 дней), не в календарных месяцах. */
  periodOffset: number
  retainedClients: number
  retainedPct: number
  offsetPeriodId: number
  /** Период ещё идёт: удержание в нём занижено по построению. */
  isCurrentPeriod: boolean
}

export interface StudioChurnRiskRow {
  /** account + client_external_id: id клиента уникален только внутри аккаунта. */
  clientKey: string
  clientExternalId: string
  clientFio: string
  visitsTotal: number
  baseVisitsTotal: number
  visits0to30: number
  visits31to60: number
  visits61to90: number
  lastVisitDate: string
  daysIdle: number
  favoriteDirection: string | null
  lastSubscriptionName: string | null
  subscriptionStatus: 'active' | 'frozen' | 'expired' | 'missing' | 'none'
  subscriptionNames: string[]
  subscriptionLastSeenDate: string | null
  subscriptionDaysMissing: number | null
}

export interface StudioVisitFrequencyRow {
  periodId: number
  periodLabel: string
  periodStart: string
  periodEnd: string
  /** Период ещё идёт — числа неполные, экран обязан это показать. */
  isCurrentPeriod: boolean
  daysElapsed: number
  daysTotal: number
  /** Клиенты, посетившие студию хотя бы раз за период. */
  activeClients: number
  totalVisits: number
  avgVisitsPerActive: number
  pctClients1Visit: number
  pctClients4Plus: number
  /** Клиенты с не менее чем одним визитом в среднем за неделю выбранного диапазона. */
  regularClients: number | null
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function numberOrZero(value: unknown): number {
  const parsed = Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

export async function getStudioRetentionCohorts(params: {
  studioInternalId: number
  cohortFrom: string
  cohortTo: string
}): Promise<StudioRetentionCohortRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_studio_retention_cohorts', {
      p_studio_internal_id: params.studioInternalId,
      p_cohort_from: params.cohortFrom,
      p_cohort_to: params.cohortTo,
    })
  )

  if (error) throw new Error(formatError(error))

  return ((data ?? []) as Array<Record<string, any>>).map((row) => ({
    cohortPeriodId: numberOrZero(row.cohort_period_id),
    cohortLabel: row.cohort_label ?? '',
    cohortSize: numberOrZero(row.cohort_size),
    periodOffset: numberOrZero(row.month_offset),
    retainedClients: numberOrZero(row.retained_clients),
    retainedPct: numberOrZero(row.retained_pct),
    offsetPeriodId: numberOrZero(row.offset_period_id),
    isCurrentPeriod: row.is_current_period === true,
  }))
}

export async function getStudioChurnRisk(params: {
  studioInternalId: number
  /** Нижняя граница простоя: раньше неё человек ещё не «спящий». */
  idleDaysMin?: number
  /** Верхняя граница: дальше неё это уже не риск оттока, а состоявшийся уход. */
  idleDaysMax?: number
}): Promise<StudioChurnRiskRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_studio_churn_risk_with_subscription', {
      p_studio_internal_id: params.studioInternalId,
      p_idle_days_min: params.idleDaysMin ?? 14,
      p_idle_days_max: params.idleDaysMax ?? 45,
    })
  )

  if (error) throw new Error(formatError(error))

  return ((data ?? []) as Array<Record<string, any>>).map((row) => ({
    clientKey: row.client_key == null ? String(row.client_external_id ?? '') : String(row.client_key),
    clientExternalId: row.client_external_id == null ? '' : String(row.client_external_id),
    clientFio: row.client_fio ?? 'Без имени',
    visitsTotal: numberOrZero(row.visits_total),
    baseVisitsTotal: numberOrZero(row.base_visits_total),
    visits0to30: numberOrZero(row.visits_0_30),
    visits31to60: numberOrZero(row.visits_31_60),
    visits61to90: numberOrZero(row.visits_61_90),
    lastVisitDate: row.last_visit_date ?? '',
    daysIdle: numberOrZero(row.days_idle),
    favoriteDirection: row.favorite_direction ?? null,
    lastSubscriptionName: row.last_subscription_name ?? null,
    subscriptionStatus: ['active', 'frozen', 'expired', 'missing'].includes(row.subscription_status)
      ? row.subscription_status
      : 'none',
    subscriptionNames: Array.isArray(row.subscription_names)
      ? row.subscription_names.filter((value: unknown): value is string => typeof value === 'string' && value.trim() !== '')
      : [],
    subscriptionLastSeenDate: row.subscription_last_seen_date ?? null,
    subscriptionDaysMissing: row.subscription_days_missing == null
      ? null
      : numberOrZero(row.subscription_days_missing),
  }))
}

export async function getStudioVisitFrequency(params: {
  studioInternalId: number
  dateFrom: string
  dateTo: string
}): Promise<StudioVisitFrequencyRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_studio_visit_frequency', {
      p_studio_internal_id: params.studioInternalId,
      p_date_from: params.dateFrom,
      p_date_to: params.dateTo,
    })
  )

  if (error) throw new Error(formatError(error))

  return ((data ?? []) as Array<Record<string, any>>).map((row) => ({
    periodId: numberOrZero(row.period_id),
    periodLabel: row.period_label ?? '',
    periodStart: row.period_start ?? '',
    periodEnd: row.period_end ?? '',
    isCurrentPeriod: row.is_current_period === true,
    daysElapsed: numberOrZero(row.days_elapsed),
    daysTotal: numberOrZero(row.days_total),
    activeClients: numberOrZero(row.active_clients),
    totalVisits: numberOrZero(row.total_visits),
    avgVisitsPerActive: numberOrZero(row.avg_visits_per_active),
    pctClients1Visit: numberOrZero(row.pct_clients_1visit),
    pctClients4Plus: numberOrZero(row.pct_clients_4plus),
    regularClients: row.regular_clients == null ? null : numberOrZero(row.regular_clients),
  }))
}
